// react
import React, { useState, useEffect } from "react";

// react-router-dom
import { useParams } from "react-router-dom";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Grid, Card, CardContent, Typography, CircularProgress } from "@mui/material";

// ui-components
import TagToolbar from "ui-components/databox/TagToolbar";
import ViewGroupsTable from "ui-components/databox/ViewGroupsTable";
import EnabledDataKeysTable from "ui-components/databox/EnabledDataKeysTable";
import ForwardRoutesTable from "ui-components/databox/ForwardRoutesTable";
import AlarmConfiguration from "ui-components/databox/AlarmConfiguration";
import AddViewGroup from "ui-components/databox/AddViewGroup";
import AddDataKey from "ui-components/databox/AddDataKey";
import NewTargetEndpoint from "ui-components/databox/NewTargetEndpoint";
import EditForwardRoute from "ui-components/databox/EditForwardRoute";
import {
  GroupAddedSuccessfulSnackbar,
  GroupAddedErrorSnackbar,
  GroupRemovedSuccessfulSnackbar,
  GroupRemovedErrorSnackbar,
  DatakeyRemovedSuccessfulSnackbar,
  ForwardRouteRemovedSuccessfulSnackbar,
  ForwardRouteRemovedErrorSnackbar,
} from "ui-components/ORFeedbacks";

// firebase
import { db } from "config/firebase";
import { doc, collection, onSnapshot, query, where } from "firebase/firestore";

// databox-controller
import getTag from "databox-controller/getTag";
import getTagOwner from "databox-controller/getTagOwner";
import getViewGroups from "databox-controller/getViewGroups";
import getViewGroupsReadOnly from "databox-controller/getViewGroupsReadOnly";
import getAuthorizedDataKeys from "databox-controller/getAuthorizedDataKeys";
import getForwardRoutes from "databox-controller/getForwardRoutes";
import isUserFollowingDatabox from "databox-controller/isUserFollowingDatabox";
import { addFollower, removeFollower } from "databox-controller/manageFollower";
import { addAlarmUser, removeAlarmUser, hasUserAlarmOn } from "databox-controller/manageAlarm";

// user-controller
import getSeenTag from "user-controller/getSeenTag";
import getUserContacts from "user-controller/getUserContacts";

// A ---------------------------------------------------------------------- M

const TagSettings = () => {
  const { user, conservSostL1 } = UserAuth();
  const { tag } = useParams();
  const { t } = useTranslation();

  // Stati principali
  const [databox, setDatabox] = useState(null);
  const [seenTag, setSeenTag] = useState(null);
  const [viewgroups, setViewGroups] = useState([]);
  const [dataKeys, setDataKeys] = useState([]);
  const [forwardRoutes, setForwardRoutes] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [alarmOn, setAlarmOn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRoute, setSelectedRoute] = useState(null);

  // Stati per i drawer
  const [openAddViewGroup, setOpenAddViewGroup] = useState(false);
  const [openAddDataKey, setOpenAddDataKey] = useState(false);
  const [openAddForwardRoute, setOpenAddForwardRoute] = useState(false);
  const [openEditForwardRoute, setOpenEditForwardRoute] = useState(false);
  const [openGroupAddedSuccessful, setOpenGroupAddedSuccessful] = useState(false);
  const [openGroupAddedError, setOpenGroupAddedError] = useState(false);
  const [openGroupRemovedSuccessful, setOpenGroupRemovedSuccessful] = useState(false);
  const [openGroupRemovedError, setOpenGroupRemovedError] = useState(false);
  const [openDatakeyRemovedSuccessful, setOpenDatakeyRemovedSuccessful] = useState(false);
  const [openForwardRouteRemovedSuccessful, setOpenForwardRouteRemovedSuccessful] = useState(false);
  const [openForwardRouteRemovedError, setOpenForwardRouteRemovedError] = useState(false);

  const fetchTagData = async () => {
    // 1. Databox
    const tagData = await getTag(tag);
    if (!tagData?.id) return;

    setDatabox(tagData);

    // 2. User-scoped seenTag
    const seenTag = await getSeenTag(user.uid, tag);
    if (!seenTag) return;

    // 3. Owner info
    const tagOwner = await getTagOwner(tag);
    const tagOwnerData = tagOwner ? await getUserContacts(tagOwner) : {};

    setSeenTag({
      ...seenTag,
      tagOwner,
      tagOwnerEmail: tagOwnerData.email || "",
    });
  };

  const fetchViewGroupsData = async () => {
    const groups = conservSostL1 ? [...(await getViewGroupsReadOnly(tag)), ...(await getViewGroups(tag))] : await getViewGroups(tag);
    setViewGroups(groups);
  };

  const fetchDataKeysData = async () => {
    const keys = await getAuthorizedDataKeys(tag);
    setDataKeys(keys.authorizedKeysArray);
  };

  const fetchForwardRoutesData = async () => {
    const routes = await getForwardRoutes(tag);
    setForwardRoutes(routes);
  };

  const fetchData = async () => {
    await Promise.all([fetchTagData(), fetchViewGroupsData(), fetchDataKeysData(), fetchForwardRoutesData()]);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();

    const currentTimestamp = Math.floor(Date.now() / 1000).toString();

    const tagsdataRef = doc(db, "tagsdata", tag);

    const collections = {
      viewgroups: collection(tagsdataRef, "viewgroups"),
      viewgroupsReadOnly: collection(tagsdataRef, "viewgroups_readonly"),
      authorizedKeys: collection(tagsdataRef, "authorized_keys"),
      forwardRoutes: collection(tagsdataRef, "forward_routes"),
    };

    const queries = {
      viewgroups: query(collections.viewgroups, where("added_on", ">=", currentTimestamp)),
      viewgroupsReadOnly: query(collections.viewgroupsReadOnly, where("added_on", ">=", currentTimestamp)),
      authorizedKeys: query(collections.authorizedKeys, where("added_on", ">=", currentTimestamp)),
      forwardRoutes: query(collections.forwardRoutes, where("added_on", ">=", currentTimestamp)),
    };

    const unsubscribes = [
      onSnapshot(queries.viewgroups, (snap) => {
        snap.docChanges().forEach((change) => {
          if (["added", "removed"].includes(change.type)) fetchViewGroupsData();
        });
      }),

      onSnapshot(queries.viewgroupsReadOnly, (snap) => {
        snap.docChanges().forEach((change) => {
          if (["added", "removed"].includes(change.type)) fetchViewGroupsData();
        });
      }),

      onSnapshot(queries.authorizedKeys, (snap) => {
        snap.docChanges().forEach((change) => {
          if (["added", "removed"].includes(change.type)) fetchDataKeysData();
        });
      }),

      onSnapshot(queries.forwardRoutes, (snap) => {
        snap.docChanges().forEach((change) => {
          if (["added", "modified", "removed"].includes(change.type)) fetchForwardRoutesData();
        });
      }),
    ];

    return () => unsubscribes.forEach((unsub) => unsub());
  }, [tag, user, conservSostL1]);

  useEffect(() => {
    const checkStatus = async () => {
      setIsFollowing(await isUserFollowingDatabox(user.uid, tag));
      setAlarmOn(await hasUserAlarmOn(user.uid, tag));
    };
    checkStatus();
  }, [user, tag]);

  const handleFollowToggle = async () => {
    if (isFollowing) await removeFollower(user.uid, tag);
    else await addFollower(user.uid, tag);
    setIsFollowing((prev) => !prev);
  };

  const handleAlarmToggle = async () => {
    if (alarmOn) await removeAlarmUser(user.uid, tag);
    else await addAlarmUser(user.uid, tag);
    setAlarmOn((prev) => !prev);
  };

  const openAddViewGroupDrawer = () => {
    setOpenAddViewGroup(true);
    window.history.pushState(null, "");
  };

  const openAddDataKeyDialog = () => {
    setOpenAddDataKey(true);
    window.history.pushState(null, "");
  };

  const openAddForwardRouteDrawer = () => {
    setOpenAddForwardRoute(true);
    window.history.pushState(null, "");
  };

  const clickEdit = (route) => {
    setSelectedRoute(route);
    setOpenEditForwardRoute(true);
    window.history.pushState(null, "");
  };

  const handleOpenGroupAddedSuccessful = () => {
    setOpenGroupAddedSuccessful(true);
    window.history.pushState(null, "");
  };

  const handleOpenGroupAddedError = () => {
    setOpenGroupAddedError(true);
    window.history.pushState(null, "");
  };

  const handleOpenGroupRemovedSuccessful = () => {
    setOpenGroupRemovedSuccessful(true);
    window.history.pushState(null, "");
  };

  const handleOpenGroupRemovedError = () => {
    setOpenGroupRemovedError(true);
    window.history.pushState(null, "");
  };

  const handleOpenDatakeyRemovedSuccessul = () => {
    setOpenDatakeyRemovedSuccessful(true);
    window.history.pushState(null, "");
  };

  const handleOpenForwardRouteRemovedSuccessful = () => {
    setOpenForwardRouteRemovedSuccessful(true);
    window.history.pushState(null, "");
  };

  const handleOpenForwardRouteRemovedError = () => {
    setOpenForwardRouteRemovedError(true);
    window.history.pushState(null, "");
  };

  if (isLoading) {
    return (
      <Grid item container justifyContent="center" mt="30%">
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container spacing={5}>
      {databox && seenTag && (
        <>
          <Grid item xs={12}>
            <TagToolbar databox={databox} tag={seenTag} isFollowing={isFollowing} handleFollowToggle={handleFollowToggle} alarmOn={alarmOn} handleAlarmToggle={handleAlarmToggle} variant="full" />
          </Grid>

          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} sm={10}>
              <Typography variant="h6" fontWeight="bold">
                {t("settings")}
              </Typography>
            </Grid>

            <Grid item container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 3, p: 1 }}>
                  <CardContent>
                    <ViewGroupsTable
                      tag={seenTag}
                      groups={viewgroups}
                      openAddViewGroupDrawer={openAddViewGroupDrawer}
                      handleOpenGroupRemovedSuccessful={handleOpenGroupRemovedSuccessful}
                      handleOpenGroupRemovedError={handleOpenGroupRemovedError}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 3, p: 1 }}>
                  <CardContent>
                    <EnabledDataKeysTable tag={seenTag} dataKeys={dataKeys} openAddDataKeyDialog={openAddDataKeyDialog} handleOpenDatakeyRemovedSuccessul={handleOpenDatakeyRemovedSuccessul} />
                  </CardContent>
                </Card>
              </Grid>

              {!conservSostL1 && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ borderRadius: 3, p: 1 }}>
                    <CardContent>
                      <ForwardRoutesTable
                        tag={seenTag}
                        forwardRoutes={forwardRoutes}
                        openAddForwardRouteDrawer={openAddForwardRouteDrawer}
                        clickEdit={clickEdit}
                        handleSuccessfulRemoved={handleOpenForwardRouteRemovedSuccessful}
                        handleErrorRemoved={handleOpenForwardRouteRemovedError}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {!conservSostL1 && (
                <Grid item xs={12}>
                  <AlarmConfiguration tag={seenTag} />
                </Grid>
              )}
            </Grid>
          </Grid>

          {openAddViewGroup && (
            <AddViewGroup tag={seenTag} open={openAddViewGroup} setOpen={setOpenAddViewGroup} handleSuccessfulAdded={handleOpenGroupAddedSuccessful} handleErrorAdded={handleOpenGroupAddedError} />
          )}

          {openGroupAddedSuccessful && <GroupAddedSuccessfulSnackbar open={openGroupAddedSuccessful} setOpen={setOpenGroupAddedSuccessful} />}

          {openGroupAddedError && <GroupAddedErrorSnackbar open={openGroupAddedError} setOpen={setOpenGroupAddedError} />}

          {openGroupRemovedSuccessful && <GroupRemovedSuccessfulSnackbar open={openGroupRemovedSuccessful} setOpen={setOpenGroupRemovedSuccessful} />}

          {openGroupRemovedError && <GroupRemovedErrorSnackbar open={openGroupRemovedError} setOpen={setOpenGroupRemovedError} />}

          {openAddDataKey && <AddDataKey tag={seenTag} dataKeys={dataKeys} open={openAddDataKey} setOpen={setOpenAddDataKey} />}

          {openDatakeyRemovedSuccessful && <DatakeyRemovedSuccessfulSnackbar open={openDatakeyRemovedSuccessful} setOpen={setOpenDatakeyRemovedSuccessful} />}

          {openAddForwardRoute && <NewTargetEndpoint tag={seenTag} open={openAddForwardRoute} setOpen={setOpenAddForwardRoute} />}

          {openEditForwardRoute && selectedRoute && <EditForwardRoute tag={seenTag} route={selectedRoute} open={openEditForwardRoute} setOpen={setOpenEditForwardRoute} />}

          {openForwardRouteRemovedSuccessful && <ForwardRouteRemovedSuccessfulSnackbar open={openForwardRouteRemovedSuccessful} setOpen={setOpenForwardRouteRemovedSuccessful} />}

          {openForwardRouteRemovedError && <ForwardRouteRemovedErrorSnackbar open={openForwardRouteRemovedError} setOpen={setOpenForwardRouteRemovedError} />}
        </>
      )}
    </Grid>
  );
};

export default TagSettings;
